import { useMemo, useState, useEffect, useCallback, Fragment } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import Header from '@features/content/Header';
import { apiSearchSlice } from '@features/api/apiSearchSlice';
import { ALIAS, CUR_NETWORK, FILTER_DATA, NETWORK, SEARCH_PARAMS, SEARCH_TYPE, SERVER_TYPE, SERVICE_ALIAS } from '@constants/index';
import { getInitialParams, setRequestToSearchParams, setSearchParamsToObj } from '@utils/index';
import { useOption } from '@hooks/useOption';
import { getLocalData } from '@features/api/apiLocalSlice';
import { useAppSelector } from '@hooks/defineRtkTypedHook';
import { selectKeyword, selectL10N, setAlias } from '@features/ui/UiSlice';
import NoData from '../../components/contents/common/NoData';
import CharacterAionArticle from '@components/contents/article/CharacterAionArticle';
import Paging from '@components/contents/common/Paging';
import { getAllWorldClassNameMap, getWorldClassNameMap, setClassIdFilterData, setClassMapStatus, setServerIdFilterData } from '@utils/setAionFilter';
import FilterOnlyUi, { setl10nKeyToTitle } from '@components/contents/common/FilterOnlyUi';
import usePrevious from '@hooks/usePrevious';
import type { ICharacterAion, IContentData, ILoaderData, l10nType } from '@type/index';

let dropdownMap: { [key: string]: any } = {};

const getServerType = (filterServerIdData: { [key: string]: string }[], serverId: string) => {
  return filterServerIdData?.filter((v) => v.value == serverId)[0]?.world || null;
};

export const CharacterAionLoader =
  (dispatch, option: IOption) =>
  async ({ request }): Promise<ICharacterAion | null> => {
    const searchParams = setRequestToSearchParams(request);

    // alias 변경 - searchParams 변경에 사용됨
    dispatch(setAlias(ALIAS.CHARACTER));

    if (!searchParams?.keyword) {
      return null;
    }

    const result =
      CUR_NETWORK === NETWORK.OPDEV
        ? await getLocalData(`${ALIAS.CHARACTER}Aion`, searchParams)
        : await dispatch(apiSearchSlice?.endpoints?.getCharacter?.initiate({ option, params: searchParams }));
    return result;
  };

const setFilterData = (serverData: { [key: string]: any }[], classData: { [key: string]: any }[]) => {
  const LIVE_SERVER = SERVER_TYPE[SERVICE_ALIAS.AION].LIVE!;
  const CLASSIC_SERVER = SERVER_TYPE[SERVICE_ALIAS.AION].CLASSIC!;
  const allClassNameMap = getAllWorldClassNameMap(classData!); // "서버 전체"일 때 직업 리스트 데이터 가공
  const liveClassNameMap = getWorldClassNameMap(LIVE_SERVER?.toUpperCase(), classData!);
  const classicClassNameMap = getWorldClassNameMap(CLASSIC_SERVER?.toUpperCase(), classData!);
  const classMapStatus = setClassMapStatus([LIVE_SERVER, CLASSIC_SERVER], allClassNameMap); // 직업 데이터 동일값인지 여부 등 상태 저장
  const filterServerIdData = setServerIdFilterData(serverData);

  return {
    allClassNameMap: setClassIdFilterData(allClassNameMap, true),
    liveClassNameMap: setClassIdFilterData(liveClassNameMap),
    classicClassNameMap: setClassIdFilterData(classicClassNameMap),
    classMapStatus,
    filterServerIdData,
  };
};

interface IFilterComponentProps {
  count: number;
  l10n: l10nType;
}
const FilterComponent = ({ count, l10n }: IFilterComponentProps) => {
  const option = useOption();
  const { serverData, classData, serviceAlias } = option;
  const LIVE_SERVER = SERVER_TYPE[SERVICE_ALIAS.AION].LIVE!;
  const CLASSIC_SERVER = SERVER_TYPE[SERVICE_ALIAS.AION].CLASSIC!;
  const { allClassNameMap, liveClassNameMap, classicClassNameMap, classMapStatus, filterServerIdData } = useMemo(
    () => setFilterData(serverData, classData),
    [serverData, classData]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = useAppSelector(selectKeyword);
  const prevKeyword = usePrevious(keyword);
  const [tempParams, setTempParams] = useState(setSearchParamsToObj(searchParams));
  const [isSetSearchParams, setIsSetSearchParams] = useState(false);
  const [activeServerId, setActiveServerId] = useState(searchParams?.get(SEARCH_PARAMS.SERVER_ID) || '');
  const [activeClassId, setActiveClassId] = useState(searchParams?.get(SEARCH_PARAMS.CLASS_ID) || '');
  const [activeRaceId, setActiveRaceId] = useState(searchParams?.get(SEARCH_PARAMS.RACE_ID) || '');
  const [activeSort, setActiveSort] = useState(searchParams?.get(SEARCH_PARAMS.SORT) || '');
  const prevActiveServerId = usePrevious(activeServerId);
  const prevActiveClassId = usePrevious(activeClassId);
  const prevActiveRaceId = usePrevious(activeRaceId);
  const prevActiveSort = usePrevious(activeSort);
  const [isChecked, setIsChecked] = useState<boolean>(searchParams?.get(SEARCH_PARAMS.SEARCH_TYPE) === SEARCH_TYPE[SERVICE_ALIAS.AION].EQUIPMENT);

  const filterClassIdData = useMemo(
    () =>
      !searchParams?.get(SEARCH_PARAMS.SERVER_ID)
        ? allClassNameMap
        : searchParams?.get(SEARCH_PARAMS.SERVER_TYPE) === CLASSIC_SERVER
          ? classicClassNameMap
          : liveClassNameMap,
    [searchParams]
  );

  const filterDataObj = useMemo(
    () =>
      serverData && classData
        ? {
            ...FILTER_DATA[SERVICE_ALIAS.AION][ALIAS.CHARACTER],
            [SEARCH_PARAMS.SERVER_ID]: filterServerIdData,
            //[SEARCH_PARAMS.CLASS_ID]: filterClassIdData
          }
        : {},
    []
  );

  const filterData = useMemo(() => Object.entries(filterDataObj), [filterDataObj]);

  // 캐릭터명 / 착용 장비
  const clickFilterType = useCallback(() => {
    const changeIsChecked = !isChecked;
    let params: { [key: string]: string } = { ...tempParams };

    if (changeIsChecked) {
      // 장비명 검색 변경 시 필터 초기화
      const initialParams = getInitialParams(serviceAlias, ALIAS.CHARACTER);
      params = { ...initialParams, [SEARCH_PARAMS.SEARCH_TYPE]: SEARCH_TYPE[SERVICE_ALIAS.AION].EQUIPMENT };
    } else {
      params && delete params[SEARCH_PARAMS.SEARCH_TYPE];
    }

    setIsChecked(changeIsChecked);
    setTempParams({ ...params });
    setIsSetSearchParams(true);
  }, [isChecked]);

  // keyword 변경 (초기 로드 시 키워드 없는 상태에선 실행되지 않도록)
  useEffect(() => {
    if (prevKeyword !== undefined && keyword && prevKeyword !== keyword) {
      const params = setSearchParamsToObj(searchParams);
      if (params) {
        setTempParams(params);
        setIsSetSearchParams(true);
      }
    }
  }, [keyword]);

  // searchParams 변경 및 url 갱신
  useEffect(() => {
    if (isSetSearchParams) {
      setSearchParams(new URLSearchParams({ ...tempParams, keyword }));
      setIsSetSearchParams(false);
    }
  }, [isSetSearchParams]);

  // serverType 갱신 -> classId dropdown 리셋(ALL/LIVE/CLASSIC 서버용 classId 리스트가 다름)
  useEffect(() => {
    const serverIdParams = searchParams.get(SEARCH_PARAMS.SERVER_ID) || filterDataObj[SEARCH_PARAMS.SERVER_ID][0]?.value;
    const classIdParams = searchParams.get(SEARCH_PARAMS.CLASS_ID) || filterClassIdData[0]?.value;
    const serverTypeParams = searchParams.get(SEARCH_PARAMS.SERVER_TYPE) || '';
    const raceIdParams = searchParams.get(SEARCH_PARAMS.RACE_ID) || filterDataObj[SEARCH_PARAMS.RACE_ID][0]?.value;
    const sortParams = searchParams.get(SEARCH_PARAMS.SORT) || filterDataObj[SEARCH_PARAMS.SORT][0]?.value;

    dropdownMap[SEARCH_PARAMS.SERVER_ID]?.activeValue([serverIdParams]);
    dropdownMap[SEARCH_PARAMS.CLASS_ID]?.reset({ data: setl10nKeyToTitle(filterClassIdData, l10n!) });
    dropdownMap[SEARCH_PARAMS.CLASS_ID]?.activeValue([!serverIdParams && classIdParams ? `${classIdParams}-${serverTypeParams}` : classIdParams]);
    dropdownMap[SEARCH_PARAMS.RACE_ID]?.activeValue([raceIdParams]);
    dropdownMap[SEARCH_PARAMS.SORT]?.activeValue([sortParams]);
  }, [filterClassIdData, searchParams]);

  // serverId 갱신 -> serverType 및 classId 갱신
  useEffect(() => {
    if (prevActiveServerId !== undefined && prevActiveServerId !== activeServerId) {
      const serverIdParams = searchParams.get(SEARCH_PARAMS.SERVER_ID) || '';
      const serverTypeParams = searchParams.get(SEARCH_PARAMS.SERVER_TYPE) || '';
      const classIdParams = searchParams.get(SEARCH_PARAMS.CLASS_ID) || '';
      let params: { [key: string]: string } = { ...tempParams }; // 같은 스코프의 여러 로직에서 tempParams를 직접 수정하면 마지막 값만 반영되어 별도 params 생성함

      if (!activeServerId) {
        // 서버 전체일 때
        if (!!classIdParams) {
          // 직업 필터 선택되어 있을 때
          const classStatus = classMapStatus?.get(classIdParams) || {};
          params = { ...params, [SEARCH_PARAMS.SERVER_TYPE]: !classStatus.isSame ? serverTypeParams : '' };
        } else {
          params = { ...params, [SEARCH_PARAMS.SERVER_TYPE]: '' };
        }
      } else {
        // 선택한 서버값이 있을 때
        const selectServerType = getServerType(filterServerIdData, activeServerId) || '';
        const isSameServerType = serverTypeParams === selectServerType; // 동일 world로 변경되는지 여부

        if (!isSameServerType) {
          // 다른 월드로 변경 시
          const classStatus = classMapStatus?.get(classIdParams) || {};
          const isSameClass = classStatus?.isSame;

          if (!classIdParams || isSameClass) {
            // 선택한 직업이 없거나, 동일한 jobId, jobName인 직업 선택 시
            params = {
              ...params,
              [SEARCH_PARAMS.SERVER_ID]: activeServerId,
              [SEARCH_PARAMS.SERVER_TYPE]: selectServerType,
            };
          } else {
            // 동일jobId & 다른 jobName / 해당 jobId 없을 때 world 변경, classId 비우기
            params = {
              ...params,
              [SEARCH_PARAMS.SERVER_TYPE]: selectServerType,
            };
            params && delete params[SEARCH_PARAMS.CLASS_ID];
          }
        }
      }

      if (activeServerId !== serverIdParams) {
        params = { ...params, [SEARCH_PARAMS.SERVER_ID]: activeServerId };
      }

      // serverId는 int값으로 보내지 않으면 api 에러 발생
      if (!activeServerId && typeof activeServerId === 'string') {
        params && delete params[SEARCH_PARAMS.SERVER_ID];
      }

      setTempParams({ ...params });
      setIsSetSearchParams(true);
    }
  }, [activeServerId]);

  // classId 갱신
  useEffect(() => {
    if (prevActiveClassId !== undefined && prevActiveClassId !== activeClassId) {
      const serverIdParams = searchParams?.get(SEARCH_PARAMS.SERVER_ID);
      const selectedClassArr = filterClassIdData?.filter((v) => v.value === activeClassId);
      let params: { [key: string]: string } = { ...tempParams };

      // 서버 변경 시 현재 선택된 직업이 해당 서버에 없을 때 "직업 전체"로 변경
      if (serverIdParams && activeClassId && selectedClassArr?.length === 0) {
        params && delete params[SEARCH_PARAMS.CLASS_ID];
        setTempParams({ ...params });
        setIsSetSearchParams(true);
        return;
      }

      if (!serverIdParams) {
        // 서버 전체일 때
        if (selectedClassArr.length > 0) {
          const selectedClass = selectedClassArr[0];
          const info = selectedClass?.value?.split('-');
          const classId = info[0] || '';
          const serverType = info[1] || '';

          params = {
            ...params,
            [SEARCH_PARAMS.SERVER_TYPE]: serverType,
            [SEARCH_PARAMS.CLASS_ID]: classId,
          };
        }
      } else {
        params = { ...params, [SEARCH_PARAMS.CLASS_ID]: activeClassId?.includes('-') ? activeClassId?.split('-')[0] : activeClassId };
      }

      // classId는 int값으로 보내지 않으면 api 에러 발생
      if (!activeClassId && typeof activeClassId === 'string') {
        params && delete params[SEARCH_PARAMS.CLASS_ID];
      }

      setTempParams({ ...params });
      setIsSetSearchParams(true);
    }
  }, [activeClassId]);

  useEffect(() => {
    if (prevActiveRaceId !== undefined && prevActiveRaceId !== activeRaceId) {
      let params: { [key: string]: string } = { ...tempParams };

      if (!activeRaceId) {
        // classId는 int값으로 보내지 않으면 api 에러 발생
        params && delete params[SEARCH_PARAMS.RACE_ID];
      } else {
        params = { ...params, [SEARCH_PARAMS.RACE_ID]: activeRaceId };
      }

      setTempParams({ ...params });
      setIsSetSearchParams(true);
    }
  }, [activeRaceId]);

  useEffect(() => {
    if (prevActiveSort !== undefined && prevActiveSort !== activeSort) {
      setTempParams({ ...tempParams, [SEARCH_PARAMS.SORT]: activeSort });
      setIsSetSearchParams(true);
    }
  }, [activeSort]);

  const setDropdownMap = useCallback(
    (data: { [key: string]: any }) => {
      dropdownMap = { ...dropdownMap, ...data };
    },
    [dropdownMap]
  );

  // serverId callback
  const serverIdChangeCallback = useCallback((name: string, activeData: { [key: string]: string }) => {
    setActiveServerId(activeData?.d1);
  }, []);

  // classId callback
  const classIdChangeCallback = useCallback((name: string, activeData: { [key: string]: string }) => {
    setActiveClassId(activeData?.d1);
  }, []);

  // raceId callback
  const raceIdChangeCallback = useCallback((name: string, activeData: { [key: string]: string }) => {
    setActiveRaceId(activeData?.d1);
  }, []);

  // sort callback
  const sortChangeCallback = useCallback((name: string, activeData: { [key: string]: string }) => {
    setActiveSort(activeData?.d1);
  }, []);

  return (
    <Header totalCount={count}>
      <div className="filter-search-type" onClick={clickFilterType}>
        <span className={`checkbox ${isChecked ? 'checked' : ''}`}></span>
        {l10n?.filter?.equipment}
      </div>
      {Array.isArray(filterData) && filterData?.length > 0 ? (
        filterData?.map((data) => (
          <FilterOnlyUi
            key={data[0]}
            filterData={setl10nKeyToTitle(data[1], l10n!)}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            changeCallback={
              data[0] === SEARCH_PARAMS.SERVER_ID
                ? serverIdChangeCallback
                : data[0] === SEARCH_PARAMS.CLASS_ID
                  ? classIdChangeCallback
                  : data[0] === SEARCH_PARAMS.RACE_ID
                    ? raceIdChangeCallback
                    : data[0] === SEARCH_PARAMS.SORT
                      ? sortChangeCallback
                      : null
            }
            setDropdownMap={setDropdownMap}
            option={{
              ...option,
              name: data[0],
              useAllBlankValue: data[1]?.filter((v) => !v?.value)?.length > 0,
            }}
          />
        ))
      ) : (
        <Fragment />
      )}
    </Header>
  );
};

interface IContentComponentProps {
  data: IContentData<ICharacterAion>;
  l10n: l10nType;
}
const ContentComponent = ({ data, l10n }: IContentComponentProps) => {
  return (
    <Fragment>
      <div className="search-content">
        <ul className="column column-2">
          {data?.list.map((item, i) => (
            <li key={i} className="search-item">
              <CharacterAionArticle item={item} l10n={l10n!} />
            </li>
          ))}
        </ul>
      </div>
      <Paging endPage={data?.pagination?.endPage} showPage={5} l10n={l10n!} />
    </Fragment>
  );
};

const CharacterAion = () => {
  const { data, isLoading, isError } = (useLoaderData() as ILoaderData<IContentData<ICharacterAion>>) || {};
  const l10n = useAppSelector(selectL10N);

  console.log('Character Data', data);

  return (
    <div className="search-result character character-aion">
      <FilterComponent count={data?.pagination?.total} l10n={l10n!} />
      {!isError && Array.isArray(data?.list) && data?.list?.length > 0 ? <ContentComponent data={data} l10n={l10n!} /> : <NoData />}
    </div>
  );
};

export default CharacterAion;
